import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { HERO_VIDEO_POSTER, HERO_VIDEO_SRC } from '../../config/heroMedia';

const FALLBACK_MS = 6500;
const LOGO_EASE = [0.2, 0.8, 0.2, 1];

const textItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.62, ease: [0.16, 1, 0.3, 1] },
  },
};

const HeroSection = ({ onHeroIntroComplete }) => {
  const [showLogo, setShowLogo] = useState(true);
  const [showText, setShowText] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const exitFiredRef = useRef(false);
  const { t } = useLanguage();
  const reducedMotion = useReducedMotion();

  const triggerExit = useCallback(() => {
    if (exitFiredRef.current) return;
    exitFiredRef.current = true;
    setShowLogo(false);
  }, []);

  useEffect(() => {
    const fallback = setTimeout(triggerExit, FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [triggerExit]);

  const handleVideoEnd = useCallback(() => {
    if (videoRef.current) videoRef.current.pause();
    triggerExit();
  }, [triggerExit]);

  useEffect(() => {
    if (videoFailed || reducedMotion) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    const kick = () => {
      el.play?.().catch(() => {});
    };
    kick();
    el.addEventListener('loadeddata', kick);
    return () => el.removeEventListener('loadeddata', kick);
  }, [videoFailed, reducedMotion]);

  const handleLogoExitComplete = useCallback(() => {
    onHeroIntroComplete?.();
    setShowText(true);
  }, [onHeroIntroComplete]);

  const handleScrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative h-screen min-h-[580px] md:min-h-[620px] max-h-[1100px] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div
        className="absolute inset-0 z-0"
        initial={false}
        animate={
          reducedMotion
            ? {}
            : {
                scale: showText ? 1 : 1.04,
              }
        }
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
      >
        {!videoFailed ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            poster={HERO_VIDEO_POSTER}
            preload="auto"
            onEnded={handleVideoEnd}
            onError={() => setVideoFailed(true)}
            onPlaying={() => setVideoPlaying(true)}
            onPause={() => setVideoPlaying(false)}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-video"
            data-playing={videoPlaying ? 'true' : 'false'}
          >
            <source src={HERO_VIDEO_SRC} type="video/mp4" />
          </video>
        ) : (
          <div className="absolute inset-0 hero-video-fallback" aria-hidden>
            <img
              src={HERO_VIDEO_POSTER}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 90% 72% at 26% 32%, rgba(0,0,0,0.48) 0%, transparent 48%),
              linear-gradient(105deg, rgba(8,8,8,0.55) 0%, rgba(8,8,8,0.06) 42%, rgba(5,5,5,0.06) 100%),
              linear-gradient(to bottom, rgba(5,5,5,0.38) 0%, rgba(6,6,6,0.06) 46%, rgba(3,3,3,0.42) 100%)
            `,
          }}
        />
      </motion.div>

      <AnimatePresence onExitComplete={handleLogoExitComplete}>
        {showLogo && (
          <motion.div
            key="hero-logo"
            className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -28 }}
            transition={{
              duration: reducedMotion ? 0.3 : 0.88,
              ease: LOGO_EASE,
            }}
            data-testid="hero-logo"
          >
            <img
              src="/sati-logo.svg"
              alt="SATI Architects"
              className="h-11 md:h-[3.25rem] w-auto max-w-[260px] md:max-w-[340px]"
              style={{ filter: 'invert(1)' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="arch-container relative z-10 py-12 sm:py-16 md:py-0 pb-28 sm:pb-24 md:pb-0">
        <div className="relative max-w-[34rem] lg:max-w-[40rem] pr-2 sm:pr-0">
          <div
            className="pointer-events-none absolute -inset-x-4 -inset-y-5 sm:-inset-x-8 sm:-inset-y-8 md:-left-10 md:-right-[15%] rounded-sm bg-gradient-to-r from-black/58 via-black/40 to-transparent opacity-[0.98]"
            aria-hidden
          />
        <motion.div
          className="relative max-w-[34rem] lg:max-w-[38rem] pr-2 sm:pr-0"
          initial="hidden"
          animate={showText ? 'show' : 'hidden'}
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.11,
                delayChildren: 0.06,
              },
            },
          }}
        >
          <motion.p
            variants={textItem}
            className="text-[10px] sm:text-[11px] text-ivory-mist/95 tracking-[0.32em] uppercase font-body mb-6 sm:mb-7 border-l border-ivory-mist/50 pl-4 drop-shadow-[0_2px_16px_rgba(0,0,0,0.65)]"
          >
            {t.hero.label}
          </motion.p>

          <motion.h1
            variants={textItem}
            className="text-[clamp(1.85rem,7.5vw,4.1rem)] font-display text-ivory-mist leading-[1.04] tracking-[-0.02em] mb-5 sm:mb-6 text-balance drop-shadow-[0_4px_32px_rgba(0,0,0,0.5)]"
          >
            {(t.hero.headlineLines || [t.hero.headline]).map((line, i) => (
              <span key={i} className="block last:mt-1">
                {line}
              </span>
            ))}
          </motion.h1>

          <motion.p
            variants={textItem}
            className="text-[clamp(1.05rem,4.2vw,1.9rem)] font-display italic text-white leading-[1.36] mb-9 sm:mb-11 max-w-[32ch] [text-shadow:0_2px_28px_rgba(0,0,0,0.82),0_1px_3px_rgba(0,0,0,0.65)]"
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.p
            variants={textItem}
            className="text-[13px] sm:text-[15px] md:text-[16px] text-white/95 leading-[1.75] font-body mb-11 sm:mb-12 max-w-lg border-l-2 border-white/70 pl-4 sm:pl-5 [text-shadow:0_2px_24px_rgba(0,0,0,0.85),0_1px_3px_rgba(0,0,0,0.6)]"
          >
            {t.hero.description}
          </motion.p>

          <motion.div
            variants={textItem}
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-5"
          >
            <a
              href="#contact"
              onClick={handleScrollToContact}
              className="inline-flex items-center justify-center min-h-[var(--sati-hero-cta-h)] px-10 sm:px-12 py-3 text-[11px] sm:text-xs tracking-[0.16em] uppercase font-semibold font-body rounded-sm bg-[hsl(var(--ivory-mist))] text-[hsl(var(--charcoal-black))] ring-2 ring-white/30 hover:brightness-[1.04] active:scale-[0.98] transition-all duration-300 shadow-[0_16px_48px_rgba(0,0,0,0.4)] hover:shadow-[0_22px_60px_rgba(0,0,0,0.45)] hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(var(--ivory-mist))]"
              data-testid="hero-cta-primary"
            >
              {t.hero.cta}
            </a>
            <Link
              to="/portfolio"
              className="inline-flex items-center justify-center min-h-[var(--sati-hero-cta-h)] px-9 sm:px-10 py-3 text-[11px] sm:text-xs tracking-[0.14em] uppercase font-semibold font-body rounded-sm border-2 border-ivory-mist/75 text-ivory-mist bg-black/35 backdrop-blur-md hover:bg-black/45 hover:border-ivory-mist active:scale-[0.98] transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.25)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>
        </motion.div>
        </div>
      </div>

      <motion.div
        className="absolute bottom-7 sm:bottom-9 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: showText ? 1 : 0 }}
        transition={{ delay: 0.55, duration: 0.55 }}
        aria-hidden
      >
        <div className="flex flex-col items-center">
          <span className="text-[9px] sm:text-[10px] text-ivory-mist/70 tracking-[0.32em] uppercase mb-3 font-body drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)]">
            {t.hero.scroll}
          </span>
          <div className="w-px h-9 sm:h-11 bg-ivory-mist/35 relative overflow-hidden rounded-full">
            {!reducedMotion && (
              <div
                className="absolute top-0 left-0 w-full h-3 bg-ivory-mist/45 rounded-full"
                style={{ animation: 'satiScrollLine 2.6s ease-in-out infinite' }}
              />
            )}
          </div>
        </div>
      </motion.div>

      <style>{`
        @keyframes satiScrollLine {
          0% { transform: translateY(-100%); opacity: 0; }
          15% { opacity: 1; }
          50% { transform: translateY(220%); opacity: 1; }
          100% { transform: translateY(220%); opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
